import { ForwardedRef, ReactNode, forwardRef } from "react";
import classNames from "classnames";
import { Button, ButtonSize, ButtonVariant } from "app/components/ui/Button/Button";
import { Link, LinkProps } from "app/components/ui/Link/Link";
import styles from "./ButtonLink.module.scss";

export type ButtonLinkProps = {
	variant?: ButtonVariant;
	size?: ButtonSize;
	fullWidth?: boolean;
	/** Icon before the label — sized by the button. */
	startIcon?: ReactNode;
	/** Icon after the label — sized by the button. */
	endIcon?: ReactNode;
	// A link is never disabled: <a> has no native disabled
} & Omit<LinkProps, "disabled">;

/**
 * A link that looks like a button: the Button styling (variant, size, icons)
 * merged onto a routing `Link` via `asChild`. `variant="borderless"` renders a
 * permanently-underlined textual link (both native `<a>` and router links).
 */
export const ButtonLink = forwardRef(
	(
		{
			variant,
			size,
			fullWidth,
			startIcon,
			endIcon,
			className,
			children,
			...linkProps
		}: ButtonLinkProps,
		ref: ForwardedRef<HTMLAnchorElement>
	) => (
		<Button
			asChild
			variant={variant}
			size={size}
			fullWidth={fullWidth}
			startIcon={startIcon}
			endIcon={endIcon}
		>
			<Link
				ref={ref}
				className={classNames({ [styles.underlined]: variant === "borderless" }, className)}
				{...linkProps}
			>
				{children}
			</Link>
		</Button>
	)
);

ButtonLink.displayName = "ButtonLink";
